import { Fragment, memo } from 'react';
import { useTranslation } from 'react-i18next';
import Slider, { Settings } from 'react-slick';

import { WebPageType } from '../../../../constants/webPageType';
import {
  CircleCheckbox,
  JoyajoyImage,
  JoyajoyImageFile,
} from '../../../Molecules';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import styles from './WebStoreProductSelectVersionTemplate.module.css';

export enum SelectVersionState {
  Purchased = 'purchased',
  Disabled = 'disalbed',
  Available = 'available',
}

type Data = {
  value: Record<string, string>;
  title: string;
  description: string;
  price: string | undefined;
  originalPrice: string | undefined;
  selected: boolean;
  state: SelectVersionState;
};

type Version = {
  groupName: string;
  data: Data[];
  hasError: boolean;
};

export type WebStoreProductSelectVersionTemplateProps = {
  name: string | undefined;
  carousel: JoyajoyImageFile[];
  versions: Version[];
  onClickItem: (value: Record<string, string>) => void;
  onClickPurchase: () => void;
};

export const WebStoreProductSelectVersionTemplate = memo(
  ({
    name,
    carousel,
    versions,
    onClickItem,
    onClickPurchase,
  }: WebStoreProductSelectVersionTemplateProps) => {
    const { t } = useTranslation();

    const settings: Settings = {
      dots: true,
      infinite: true,
      speed: 1000,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 5000,
      arrows: true,
      adaptiveHeight: false,
    };

    return (
      <>
        <div className={styles.name}>{name}</div>
        <hr className={styles.pageDivider} />
        <div className={styles.versionDetail}>
          <div className={styles.carouselContainer}>
            <Slider {...settings}>
              {carousel.map((src, index) => (
                <div key={index} className={styles.slideItem}>
                  <JoyajoyImage {...src} />
                </div>
              ))}
            </Slider>
          </div>
          <div className={styles.versionName}>{versions[0]?.groupName}</div>
          {versions.map((version, versionIndex) => (
            <div className={styles.textArea} key={versionIndex}>
              <div
                className={`${styles.versionContainer} ${
                  version.hasError ? styles.redBorder : ''
                }`}
              >
                <ul className={styles.versionList}>
                  {version.data.map((item, index) => (
                    <Fragment key={index}>
                      <li
                        className={`${styles.versionItem} ${
                          item.state === SelectVersionState.Disabled
                            ? styles.versionItemDisabled
                            : item.state === SelectVersionState.Purchased
                            ? styles.versionItemPurchased
                            : ''
                        }`}
                        onClick={() => {
                          if (item.state === SelectVersionState.Available) {
                            onClickItem(item.value);
                          }
                        }}
                      >
                        <div className={styles.textItem}>
                          <div className={styles.itemTitle}>{item.title}</div>
                          {item.price !== undefined ? (
                            <div className={styles.priceContainer}>
                              {item.price !== undefined ? (
                                <div className={styles.productPrice}>
                                  {item.price}
                                </div>
                              ) : null}
                              {item.originalPrice !== undefined ? (
                                <div className={styles.originalPrice}>
                                  {item.originalPrice}
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                          {item.description ? (
                            <div className={styles.itemDescription}>
                              {item.description}
                            </div>
                          ) : null}
                        </div>
                        {item.state === SelectVersionState.Purchased ? (
                          <div className={styles.disabled}>
                            {t('Purchased')}
                          </div>
                        ) : (
                          <CircleCheckbox
                            checked={item.selected}
                            webPageType={WebPageType.JOYAJOY}
                          />
                        )}
                      </li>
                      {index < version.data.length - 1 && <hr />}
                    </Fragment>
                  ))}
                </ul>
              </div>
            </div>
          ))}
          <div className={styles.shareButtonContainer}>
            <button className={styles.shareButton} onClick={onClickPurchase}>
              {t('Purchase')}
            </button>
          </div>
        </div>
      </>
    );
  },
);
